import React, { useState } from 'react';
import { Pencil, Trash2, Send, Paperclip, Download, Image as ImageIcon, FileText, ExternalLink, Reply, SmilePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RichEditor, RichView } from '@/components/project/RichEditor';
import { FileUploadButton } from '@/components/FileUpload';
import { UserAvatar, fmtDT, fmtSize } from '@/components/project/projectUtils';
import { fileUrl } from '@/lib/upload';
const attUrl = (a, download = false) => (a.content_type === 'link' ? a.url : fileUrl({ id: a.id, name: a.name, is_public: false }, { download }));

const REACTIONS = ['👍', '❤️', '🎉', '✅', '👀'];

const CommentItem = ({
  cm,
  replies,
  me,
  members,
  canEdit,
  isOwner,
  lang,
  call,
  refresh,
  editing,
  setEditing,
  editBody,
  setEditBody,
  setEditMentions,
  saveEdit,
  onReply,
  depth = 0,
}) => {
  const id = lang === 'id';
  const react = async (emoji) => {
    await call('post', `/project/comments/${cm.id}/react`, { emoji });
    refresh();
  };
  const rx = Object.entries(cm.reactions || {}).filter(([, ids]) => ids.length);
  return (
    <div className={`flex gap-2.5 ${depth ? 'ml-8 mt-2' : ''}`} data-testid={`comment-${cm.id}`}>
      <UserAvatar u={cm.user} size={depth ? 'sm' : 'md'} />
      <div className="min-w-0 flex-1">
        <div className="rounded-xl border border-border bg-white px-3 py-2">
          <div className="mb-0.5 flex items-center gap-2 text-xs">
            <span className="font-semibold">{cm.user?.name}</span>
            <span className="text-muted-foreground">
              {fmtDT(cm.created_at, lang)}
              {cm.is_edited && ` · ${id ? 'diedit' : 'edited'}`}
            </span>
            {cm.user_id === me && canEdit && (
              <button
                type="button"
                onClick={() => {
                  setEditing(cm.id);
                  setEditBody(cm.body);
                  setEditMentions(cm.mention_user_ids || []);
                }}
                className="ml-auto rounded p-0.5 text-muted-foreground hover:text-foreground"
                aria-label="Edit"
                data-testid={`comment-edit-${cm.id}`}
              >
                <Pencil size={12} />
              </button>
            )}
            {(cm.user_id === me || isOwner) && canEdit && (
              <button
                type="button"
                onClick={() => call('delete', `/project/comments/${cm.id}`).then(refresh)}
                className={`rounded p-0.5 text-muted-foreground hover:text-rose-600 ${cm.user_id !== me ? 'ml-auto' : ''}`}
                aria-label="Delete"
                data-testid={`comment-delete-${cm.id}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
          {editing === cm.id ? (
            <div className="space-y-1.5">
              <RichEditor
                value={editBody}
                onChange={setEditBody}
                onMentionsChange={setEditMentions}
                members={members}
                minimal
                autoFocus
                testid="comment-edit-editor"
              />
              <div className="flex gap-1.5">
                <Button size="sm" className="h-7 rounded-lg" onClick={saveEdit} data-testid="comment-edit-save">
                  {id ? 'Simpan' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-lg" onClick={() => setEditing(null)}>
                  {id ? 'Batal' : 'Cancel'}
                </Button>
              </div>
            </div>
          ) : (
            <RichView html={cm.body} />
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
          {rx.map(([e, ids]) => (
            <button
              type="button"
              key={e}
              disabled={!canEdit}
              onClick={() => react(e)}
              className={`rounded-full border px-1.5 py-0.5 ${ids.includes(me) ? 'border-primary bg-primary/10' : 'border-border bg-white'}`}
              title={ids.map((u) => members.find((m) => m.id === u)?.name || '').join(', ')}
              data-testid={`reaction-${cm.id}-${e}`}
            >
              {e} {ids.length}
            </button>
          ))}
          {canEdit && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="rounded-full border border-dashed border-border px-1.5 py-0.5 text-muted-foreground hover:text-foreground"
                  aria-label="React"
                  data-testid={`react-add-${cm.id}`}
                >
                  <SmilePlus size={12} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="flex w-auto gap-1 rounded-xl p-1.5">
                {REACTIONS.map((e) => (
                  <button
                    type="button"
                    key={e}
                    onClick={() => react(e)}
                    className="rounded-lg px-1.5 py-1 text-base hover:bg-muted"
                    data-testid={`react-pick-${cm.id}-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
          {canEdit && depth === 0 && (
            <button
              type="button"
              onClick={() => onReply(cm)}
              className="ml-1 inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              data-testid={`comment-reply-${cm.id}`}
            >
              <Reply size={12} />
              {id ? 'Balas' : 'Reply'}
            </button>
          )}
        </div>
        {replies}
      </div>
    </div>
  );
};

export const Comments = ({ card, me, members, canEdit, isOwner, lang, call, refresh }) => {
  const id = lang === 'id';
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState([]);
  const [key, setKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editMentions, setEditMentions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [composing, setComposing] = useState(false);
  const send = async () => {
    if (!body) return;
    setBusy(true);
    try {
      await call('post', `/project/cards/${card.id}/comments`, { body, mention_user_ids: mentions, parent_id: replyTo?.id || null });
      setBody('');
      setMentions([]);
      setReplyTo(null);
      setKey((k) => k + 1);
      refresh();
    } catch (e) {
      /* toast */
    } finally {
      setBusy(false);
    }
  };
  const saveEdit = async () => {
    await call('patch', `/project/comments/${editing}`, { body: editBody, mention_user_ids: editMentions });
    setEditing(null);
    refresh();
  };
  const roots = (card.comments || []).filter((c) => !c.parent_id);
  const repliesOf = (pid) => (card.comments || []).filter((c) => c.parent_id === pid);
  const orphans = (card.comments || []).filter((c) => c.parent_id && !roots.some((r) => r.id === c.parent_id));
  const itemProps = {
    me,
    members,
    canEdit,
    isOwner,
    lang,
    call,
    refresh,
    editing,
    setEditing,
    editBody,
    setEditBody,
    setEditMentions,
    saveEdit,
    onReply: setReplyTo,
  };
  return (
    <div className="space-y-3" data-testid="card-comments">
      {canEdit && (
        <div className="space-y-1.5">
          {replyTo && (
            <div className="flex items-center gap-2 rounded-lg bg-muted px-2 py-1 text-[11px]" data-testid="comment-replying">
              <Reply size={11} />
              {id ? 'Membalas' : 'Replying to'} <b>{replyTo.user?.name}</b>
              <button type="button" onClick={() => setReplyTo(null)} className="ml-auto" aria-label="Cancel reply">
                <X size={11} />
              </button>
            </div>
          )}
          {!composing && !replyTo ? (
            <button type="button" onClick={() => setComposing(true)} className="w-full rounded-xl border border-border bg-white px-3 py-2 text-left text-sm text-muted-foreground hover:border-primary/40" data-testid="comment-composer-open">
              {id ? 'Tulis komentar… gunakan @ untuk mention' : 'Write a comment… use @ to mention'}
            </button>
          ) : (
            <>
              <RichEditor
                key={key}
                value={body}
                onChange={setBody}
                onMentionsChange={setMentions}
                members={members}
                autoFocus
                placeholder={id ? 'Tulis komentar… gunakan @ untuk mention' : 'Write a comment… use @ to mention'}
                testid="comment-editor"
              />
              <div className="flex justify-end">
                <Button size="sm" className="rounded-xl" onClick={send} disabled={busy || !body} data-testid="comment-submit">
                  <Send size={13} className="mr-1" />
                  {replyTo ? (id ? 'Kirim balasan' : 'Send reply') : id ? 'Kirim' : 'Send'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {(card.comments || []).length === 0 && <p className="text-xs text-muted-foreground">{id ? 'Belum ada komentar.' : 'No comments yet.'}</p>}
      {[...roots, ...orphans].map((cm) => (
        <CommentItem key={cm.id} cm={cm} replies={repliesOf(cm.id).map((r) => <CommentItem key={r.id} cm={r} replies={null} depth={1} {...itemProps} />)} {...itemProps} />
      ))}
    </div>
  );
};

export const Attachments = ({ card, canEdit, lang, call, refresh }) => {
  const id = lang === 'id';
  const add = async (att) => {
    await call('post', `/project/cards/${card.id}/attachments`, { attachment_id: att.id });
    refresh();
  };
  return (
    <div className="space-y-2" data-testid="card-attachments">
      {(card.attachments || []).length === 0 && <p className="text-xs text-muted-foreground">{id ? 'Belum ada lampiran.' : 'No attachments yet.'}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {(card.attachments || []).map((a) => (
          <div key={a.id} className="flex items-center gap-2.5 rounded-xl border border-border bg-white p-2" data-testid={`attachment-${a.id}`}>
            {a.is_image ? (
              <a href={attUrl(a)} target="_blank" rel="noreferrer">
                <img src={attUrl(a)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              </a>
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                {a.content_type === 'link' ? <ExternalLink size={18} /> : a.is_image ? <ImageIcon size={18} /> : <FileText size={18} />}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-[11px] text-muted-foreground">
                {a.size ? fmtSize(a.size) + ' · ' : ''}
                {a.uploaded_by_name} · {fmtDT(a.uploaded_at, lang)}
              </div>
            </div>
            <a
              href={attUrl(a, true)}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Download"
              data-testid={`attachment-download-${a.id}`}
            >
              <Download size={14} />
            </a>
            {canEdit && (
              <button
                type="button"
                onClick={() => call('delete', `/project/cards/${card.id}/attachments/${a.id}`).then(refresh)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-rose-600"
                aria-label="Remove"
                data-testid={`attachment-remove-${a.id}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <FileUploadButton kind="project" refId={card.id} multiple onUploaded={add} testid="card-attach-upload">
          <Paperclip size={14} className="mr-1.5" />
          {id ? 'Unggah lampiran' : 'Upload attachment'}
        </FileUploadButton>
      )}
    </div>
  );
};
